import { Alert, Backdrop } from '@mui/material';
import { Box } from '@mui/system';
import UPButton from 'components/button';
import UPInput from 'components/input';
import { onError } from 'modules/contracts/modals/helpers';
import { OnboardingImage } from 'modules/onboarding/styles';
import React from 'react';
import { Controller, SubmitHandler, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useAppDispatch } from 'store';
import { getExistingEmployeeToken } from 'store/actions/employee-actions';
import {
  EXISTING_EMPLOYEE_ONBOARDING__EMAIL,
  EXISTING_EMPLOYEE_ONBOARDING__EMAIL_PLACEHOLDER,
  EXISTING_EMPLOYEE_ONBOARDING__INSS,
  EXISTING_EMPLOYEE_ONBOARDING__INSS_PLACEHOLDER,
  EXISTING_EMPLOYEE_ONBOARDING__LOGIN_CONFIRM,
  EXISTING_EMPLOYEE_ONBOARDING__LOGIN_ERROR,
  EXISTING_EMPLOYEE_ONBOARDING__LOGIN_EXISTING_PASSWORD,
  EXISTING_EMPLOYEE_ONBOARDING__LOGIN_EXISTING_PASSWORD_PLACEHOLDER,
  EXISTING_EMPLOYEE_ONBOARDING__LOGIN_SUBTITLE,
  EXISTING_EMPLOYEE_ONBOARDING__LOGIN_TITLE,
  GENERAL__CANCEL_BUTTON,
} from 'translations/constants';
import IOnboardingLogin from 'types/employee/IOnboardingLogin';
import { getInputErrorText } from 'utils/helpers';
import {
  ModalFooter,
  ModalHeader,
  ModalWrapper,
  OLContainer,
  OLContentContainer,
  OLModalContainer,
  OLSubtitle,
  OLTitle,
} from './styles';

const OnboardingLoginInitialState: IOnboardingLogin = {
  email: '',
  password: '',
  inss: '',
};

const OnboardingLogin = ({ open, onClose }: { open: boolean; onClose: () => void }) => {
  const [t] = useTranslation();
  const {
    handleSubmit,
    control,
    getValues,
    clearErrors,
    formState: { errors, isValid, isSubmitting },
  } = useForm<IOnboardingLogin>({
    defaultValues: OnboardingLoginInitialState,
    mode: 'onChange',
  });
  const dispatch = useAppDispatch();
  const [displayError, setDisplayError] = React.useState(false);

  const onSubmit: SubmitHandler<IOnboardingLogin> = async () => {
    setDisplayError(false);
    const values = getValues();
    values.inss = values.inss.split('.').join('').split('-').join('');
    const loginResult = await dispatch(getExistingEmployeeToken(values)).unwrap();
    if (loginResult) {
      onClose();
    } else {
      setDisplayError(true);
    }
  };

  return (
    <Backdrop sx={{ zIndex: '1', backgroundColor: 'transparent' }} id="TestMe" open={open}>
      <ModalWrapper open={open} onClose={onClose}>
        <OLModalContainer>
          <ModalHeader>
            <OLTitle>{t(EXISTING_EMPLOYEE_ONBOARDING__LOGIN_TITLE)}</OLTitle>
            <OLSubtitle>{t(EXISTING_EMPLOYEE_ONBOARDING__LOGIN_SUBTITLE)}</OLSubtitle>
          </ModalHeader>
          <OLContentContainer>
            <OnboardingImage />
            <Box display="flex" flexDirection="column" alignItems="center" width="100%" padding="0px 32px 32px 32px">
              <OLContainer>
                <Controller
                  name="email"
                  control={control}
                  rules={{ required: true }}
                  render={({ field }) => (
                    <UPInput
                      value={field.value}
                      onChange={value => {
                        field.onChange(value);
                        clearErrors('email');
                      }}
                      label={t(EXISTING_EMPLOYEE_ONBOARDING__EMAIL)}
                      placeholder={t(EXISTING_EMPLOYEE_ONBOARDING__EMAIL_PLACEHOLDER)}
                      error={!!errors.email}
                      helperText={getInputErrorText(errors, 'email', undefined, 100)}
                      inputClassName="white-background"
                      autoComplete="off"
                    />
                  )}
                />
                <Controller
                  name="inss"
                  control={control}
                  rules={{ required: true }}
                  render={({ field }) => (
                    <UPInput
                      value={field.value}
                      onChange={field.onChange}
                      label={t(EXISTING_EMPLOYEE_ONBOARDING__INSS)}
                      placeholder={t(EXISTING_EMPLOYEE_ONBOARDING__INSS_PLACEHOLDER)}
                      error={!!errors.inss}
                      helperText={getInputErrorText(errors, 'inss')}
                      inputClassName="white-background"
                    />
                  )}
                />
                <Controller
                  name="password"
                  control={control}
                  rules={{ required: true }}
                  render={({ field }) => {
                    return (
                      <UPInput
                        value={field.value}
                        onChange={e => {
                          field.onChange(e);
                        }}
                        label={t(EXISTING_EMPLOYEE_ONBOARDING__LOGIN_EXISTING_PASSWORD)}
                        placeholder={t(EXISTING_EMPLOYEE_ONBOARDING__LOGIN_EXISTING_PASSWORD_PLACEHOLDER)}
                        type="password"
                        error={!!errors.password}
                        autoComplete="new-password"
                        inputClassName="white-background"
                      />
                    );
                  }}
                />
              </OLContainer>
            </Box>
          </OLContentContainer>
          {displayError && (
            <Alert severity="error" icon={false} variant="filled">
              {t(EXISTING_EMPLOYEE_ONBOARDING__LOGIN_ERROR)}
            </Alert>
          )}
          <ModalFooter>
            <Box>
              <UPButton
                text={t(EXISTING_EMPLOYEE_ONBOARDING__LOGIN_CONFIRM)}
                onClick={() => handleSubmit(onSubmit, onError)()}
                disabled={isSubmitting || !isValid}
              />
            </Box>
            <Box sx={{ marginLeft: '12px' }}>
              <UPButton text={t(GENERAL__CANCEL_BUTTON)} onClick={onClose} outlined />
            </Box>
          </ModalFooter>
        </OLModalContainer>
      </ModalWrapper>
    </Backdrop>
  );
};

export default OnboardingLogin;
